import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    FormControl,
    Tooltip,
    useTheme,
    Alert,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField
} from "@mui/material";
import { useAtom } from "jotai";
import {
    bookingDetailsAtom,
    getSelectedLocationAtom,
    getSelectedServiceAtom,
    showOnlyWhoIsAvailableDialogAtom,
    whoIsAvailableSlotsAtom,
    whoIsAvailableSlotsNearestDateSortedAtom,
    whoIsAvailableTimezoneAtom
} from "../atoms/booking";
import {
    CLIENT_LOCATION_ID,
    DEFAULT_BUFFER_TIME_MINS,
    DEFAULT_RRULE,
    SLOT_TYPES
} from "../utils/Constants";
import React, { useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, ThemeProvider } from "@mui/system";
import { companyAtom } from "../atoms/atoms";
import { addDays, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import {
    formatDateInfoAttributes,
    getFullTimeZoneNameForTimeZone
} from "../modules/TimeService";
import { DialogTheme } from "../styles/DialogTheme";
import LeafSvg from "../images/LeafSvg";
import WarningTriangleIcon from "../images/WarningTriangleIcon.svg";
import { SuggestedSlotsTheme } from "../styles/SuggestedSlotsTheme";
import InfoIcon from "@mui/icons-material/Info";
import { isCountryImperial, truncateName } from "../utils";
import { useQuery } from "@tanstack/react-query";
import { postApiWithStatusCode } from "../modules/AmplifyServices";
import { rfcRRuleAtom } from "../atoms/recurringSettings";
import { userPageSnackbarAtom } from "../atoms/usersTable";
import CustomAnimatedSpinner from "../components/CustomAnimatedSpinner";
import CustomTimePicker from "../user/components/CustomTimePicker";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enUS } from "date-fns/locale";

export const SuggestedTimesDialog = ({
    open,
    onClose,
    sessionIndex,
    onDateTimeChange,
    handleClientAutoFocus,
    handleSelectedCustomDateTime,
    onRegularDateTimeChange
}) => {
    const theme = useTheme();
    const [, setWhoIsAvailableSlots] = useAtom(whoIsAvailableSlotsAtom);
    const [, setWhoIsAvailableTimezone] = useAtom(whoIsAvailableTimezoneAtom);

    const handleClose = () => {
        setWhoIsAvailableSlots([]);
        setWhoIsAvailableTimezone(null);
        onClose();
    };

    return (
        <ThemeProvider theme={DialogTheme(theme)}>
            <Dialog open={open} onClose={handleClose}>
                <SuggestedSlotsFormContent
                    onClose={handleClose}
                    sessionIndex={sessionIndex}
                    onDateTimeChange={onDateTimeChange}
                    handleClientAutoFocus={handleClientAutoFocus}
                    handleSelectedCustomDateTime={handleSelectedCustomDateTime}
                    onRegularDateTimeChange={onRegularDateTimeChange}
                />
            </Dialog>
        </ThemeProvider>
    );
};

const SuggestedSlotsFormContent = ({
    onClose,
    sessionIndex,
    onDateTimeChange,
    handleClientAutoFocus,
    handleSelectedCustomDateTime,
    onRegularDateTimeChange
}) => {
    const [{ isRepeating, selectedPackage, sessions, provider }] =
        useAtom(bookingDetailsAtom);
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);
    const [selectedLocation] = useAtom(getSelectedLocationAtom);
    const [selectedService] = useAtom(getSelectedServiceAtom);
    const [company] = useAtom(companyAtom);
    const SHORTEST_TRAVEL_TIME_FILTER = "shortestTravelTime";
    const NEAREST_DATE_FILTER = "nearestDate";
    const theme = useTheme();
    const [showPartialAvailabilityDialog, setShowPartialAvailabilityDialog] =
        useState(false);
    const [whoIsAvailableSlots, setWhoIsAvailableSlots] = useAtom(
        whoIsAvailableSlotsAtom
    );
    const [, setWhoIsAvailableTimezone] = useAtom(whoIsAvailableTimezoneAtom);
    const [whoIsAvailableTimezone] = useAtom(whoIsAvailableTimezoneAtom);
    const [whoIsAvailableSlotsNearestDateSorted] = useAtom(
        whoIsAvailableSlotsNearestDateSortedAtom
    );
    const [rrule] = useAtom(rfcRRuleAtom);
    const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);
    const [filterOption, setFilterOption] = useState(NEAREST_DATE_FILTER);
    const rangeDefaultStart = new Date(
        sessionIndex !== null && sessionIndex > 0
            ? sessions[sessionIndex].dateTime
            : sessions[0].dateTime
    );
    const [selectedDate, setSelectedDate] = useState(rangeDefaultStart);
    const [selectedTime, setSelectedTime] = useState(rangeDefaultStart);
    const [openDate, setOpenDate] = useState(false);
    const showSlots = provider && selectedService && selectedLocation;
    const distanceUnit =
        selectedLocation?.countrycode3166alpha2?.toUpperCase() === "US" ||
        selectedLocation?.countrycode3166alpha2?.toUpperCase() === "GB"
            ? "mi"
            : "km";

    const displayedSlots =
        filterOption === NEAREST_DATE_FILTER
            ? whoIsAvailableSlotsNearestDateSorted
            : whoIsAvailableSlots;

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            // Update time to match the selected date
            const newDateTime = new Date(date);
            newDateTime.setHours(selectedTime.getHours());
            newDateTime.setMinutes(selectedTime.getMinutes());
            setSelectedTime(newDateTime);
        }
    };

    const handleTimeChange = (time) => {
        if (time) {
            const timeObj = new Date(time);
            setSelectedTime(timeObj);
            // Update date to match the selected time
            const newDateTime = new Date(selectedDate);
            newDateTime.setHours(timeObj.getHours());
            newDateTime.setMinutes(timeObj.getMinutes());
            setSelectedDate(newDateTime);
        }
    };

    const handleRegularConfirm = () => {
        // Combine date and time into a single Date object
        const combinedDateTime = new Date(selectedDate);
        combinedDateTime.setHours(selectedTime.getHours());
        combinedDateTime.setMinutes(selectedTime.getMinutes());
        combinedDateTime.setSeconds(0);
        combinedDateTime.setMilliseconds(0);

        onRegularDateTimeChange(combinedDateTime);
    };

    const { isLoading: isLoadingSlots, isFetching: isFetchingSlots } = useQuery(
        {
            queryKey: ["get-slots", provider?.id || null, selectedDate],
            queryFn: async () => {
                if (provider) {
                    try {
                        const locationTimeZone = provider.timezone;
                        setWhoIsAvailableTimezone(locationTimeZone);

                        const startDate = new Date(selectedDate).setHours(
                            0,
                            0,
                            0,
                            0
                        );
                        const { dtstamp_str: startDateTimeIso } =
                            formatDateInfoAttributes(
                                startDate,
                                selectedService?.minutes || null,
                                locationTimeZone
                            );

                        const endDate = addDays(
                            new Date(selectedDate),
                            7
                        ).setHours(23, 59, 59, 0);
                        const { dtstamp_str: endDateTimeIso } =
                            formatDateInfoAttributes(
                                endDate,
                                selectedService?.minutes || null,
                                locationTimeZone
                            );

                        const { body: slotData, statusCode } =
                            await postApiWithStatusCode(
                                "MB-rest-api",
                                "/v1/mb/search/slots",
                                {
                                    body: {
                                        companyId: company.id,
                                        targetGeoLocation: [
                                            selectedLocation.latitude,
                                            selectedLocation.longitude
                                        ],
                                        isCompanyLocation:
                                            selectedLocation.id !==
                                            CLIENT_LOCATION_ID,
                                        companyLocationId:
                                            selectedLocation.id !==
                                            CLIENT_LOCATION_ID
                                                ? selectedLocation.id
                                                : null,
                                        targetServiceId: selectedService.id,
                                        startDate: startDateTimeIso,
                                        endDate: endDateTimeIso,
                                        timezone: locationTimeZone,
                                        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
                                        skills: [],
                                        recurring: isRepeating,
                                        rrule: rrule ?? DEFAULT_RRULE,
                                        package: selectedPackage ? true : false,
                                        providers: [provider.id]
                                    }
                                }
                            );

                        if (
                            statusCode === 200 &&
                            slotData?.timeSlots?.length > 0
                        ) {
                            setWhoIsAvailableSlots(slotData?.timeSlots);
                        } else if (
                            statusCode === 200 &&
                            slotData?.timeSlots?.length === 0
                        ) {
                            setWhoIsAvailableSlots([]);
                            setUserPageSnackbar({
                                open: true,
                                message: "No slots found",
                                severity: "error"
                            });
                        } else {
                            setUserPageSnackbar({
                                open: true,
                                message: "Error fetching slots",
                                severity: "error"
                            });
                        }

                        return slotData;
                    } catch (e) {
                        console.log(e);
                    }
                }
            },
            onSuccess: ({ timeSlots, timezone }) => {
                setWhoIsAvailableSlots(timeSlots);
                setWhoIsAvailableTimezone(timezone);
            }
        }
    );

    const getSlotType = () => {
        if (displayedSlots[selectedSlotIndex]?.travelType === "ON-EMPTY-DAY") {
            return SLOT_TYPES.SUGGESTED_SLOT_EMPTY_DAY;
        } else if (
            displayedSlots[selectedSlotIndex]?.travelTimeMins <= 5 ||
            displayedSlots[selectedSlotIndex]?.travelType?.startsWith(
                "SAME-LOCATION"
            )
        ) {
            return SLOT_TYPES.SUGGESTED_SLOT_OPTIMIZED;
        } else {
            return SLOT_TYPES.SUGGESTED_SLOT;
        }
    };

    return (
        <>
            <DialogTitle sx={{ paddingBottom: "32px" }}>
                Date and time
            </DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "27px",
                    padding: "0px",
                    paddingBottom: "1px"
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "18px",
                        [theme.breakpoints.up("md")]: {
                            padding: "0px 36px"
                        }
                    }}
                >
                    {whoIsAvailableTimezone && (
                        <Alert
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "10px 0px",
                                border: `1px solid ${theme.palette.info.main}`,
                                marginBottom: "0px",
                                backgroundColor: theme.palette.info.lightest,
                                textAlign: "center"
                            }}
                            severity="info"
                            icon={<InfoIcon />}
                        >
                            <Typography
                                color={theme.palette.info.main}
                                fontWeight={500}
                                display={"inline"}
                            >
                                Times shown in{" "}
                                {getFullTimeZoneNameForTimeZone(
                                    new Date(),
                                    whoIsAvailableTimezone
                                ) || ""}
                            </Typography>
                        </Alert>
                    )}
                    {showSlots && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                paddingTop: "8px"
                            }}
                        >
                            <Typography>
                                Select a custom date and time
                            </Typography>
                        </Box>
                    )}

                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={enUS}
                    >
                        <Grid container spacing={2.25}>
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                sx={{ paddingTop: "8px !important" }}
                            >
                                <DatePicker
                                    label="Date"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    open={openDate}
                                    onOpen={() => setOpenDate(true)}
                                    onClose={() => {
                                        setOpenDate(false);
                                    }}
                                    inputFormat="MMM dd, yyyy"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            inputProps={{
                                                ...params.inputProps,
                                                readOnly: true
                                            }}
                                            sx={{
                                                "& .MuiInputAdornment-root": {
                                                    display: "none"
                                                }
                                            }}
                                            onClick={(e) => {
                                                setOpenDate(true);
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                sx={{ paddingTop: "8px !important" }}
                            >
                                <CustomTimePicker
                                    id="start-time"
                                    name={`timeBlocks.startTime`}
                                    type="startTime"
                                    value={selectedTime}
                                    onChange={handleTimeChange}
                                    outlinedInputSx={{
                                        padding: "7.5px 14px",
                                        color: theme.palette.text.main
                                    }}
                                    step={5}
                                    label={"Time"}
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>

                    {showSlots && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                paddingTop: "8px"
                            }}
                        >
                            <Typography sx={{ fontWeight: "700" }}>
                                OR select an optimized time below:
                            </Typography>
                        </Box>
                    )}
                </Box>
                {(isLoadingSlots || isFetchingSlots) && provider ? (
                    <CustomAnimatedSpinner
                        loading={isLoadingSlots || isFetchingSlots}
                    />
                ) : (
                    <>
                        {displayedSlots?.length > 0 ? (
                            <>
                                {showSlots && (
                                    <>
                                        {/* Sort by Filter Options */}

                                        <Box
                                            sx={{
                                                height: "14px",
                                                [theme.breakpoints.up("md")]: {
                                                    padding: "0px 36px",
                                                    height: "36px"
                                                }
                                            }}
                                        >
                                            <FormControl component="fieldset">
                                                <RadioGroup
                                                    row
                                                    value={filterOption}
                                                    onChange={(e) =>
                                                        setFilterOption(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <FormControlLabel
                                                        value={
                                                            NEAREST_DATE_FILTER
                                                        }
                                                        control={
                                                            <Radio size="small" />
                                                        }
                                                        label="Sort by nearest date"
                                                        sx={{
                                                            "& .MuiFormControlLabel-label":
                                                                {
                                                                    fontSize:
                                                                        "14px"
                                                                }
                                                        }}
                                                    />
                                                    <FormControlLabel
                                                        value={
                                                            SHORTEST_TRAVEL_TIME_FILTER
                                                        }
                                                        control={
                                                            <Radio size="small" />
                                                        }
                                                        label="Sort by least travel time"
                                                        sx={{
                                                            "& .MuiFormControlLabel-label":
                                                                {
                                                                    fontSize:
                                                                        "14px"
                                                                }
                                                        }}
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Box>

                                        {sessions.length &&
                                            sessions[0].locationTzDateTimeIso &&
                                            showSlots && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "center",
                                                        flexDirection: "column",
                                                        [theme.breakpoints.up(
                                                            "md"
                                                        )]: {
                                                            padding: "0px 36px"
                                                        }
                                                    }}
                                                >
                                                    <Typography>
                                                        Current Selection:
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            whiteSpace:
                                                                "pre-wrap",
                                                            fontWeight: "700"
                                                        }}
                                                    >
                                                        {`${formatInTimeZone(
                                                            parseISO(
                                                                sessions[0]
                                                                    .locationTzDateTimeIso
                                                            ),
                                                            sessions[0].locationTzDateTimeIso.match(
                                                                /[+-]\d{2}:\d{2}$/
                                                            )[0],
                                                            "E, MMM dd – h:mm aa"
                                                        )}`}
                                                    </Typography>
                                                </Box>
                                            )}

                                        {/* This Box makes the Grid scrollable */}
                                        <Box
                                            sx={{
                                                overflowY: "unset",
                                                padding: "8px 0px 0px 14px",
                                                [theme.breakpoints.down("md")]:
                                                    {
                                                        padding:
                                                            "8px 0px 0px 0px"
                                                    },
                                                marginTop: "-8px",
                                                display: "flex",
                                                justifyContent: "center"
                                            }}
                                        >
                                            <Grid
                                                container
                                                spacing={2.25}
                                                sx={{
                                                    gap: "18px",
                                                    margin: "0px",
                                                    maxWidth: "378px",
                                                    justifyContent: "center"
                                                }}
                                            >
                                                {displayedSlots.map(
                                                    (slot, index) => (
                                                        <ThemeProvider
                                                            theme={SuggestedSlotsTheme(
                                                                {
                                                                    palette:
                                                                        theme.palette
                                                                }
                                                            )}
                                                        >
                                                            <Box
                                                                display={"flex"}
                                                                alignItems={
                                                                    "center"
                                                                }
                                                                width={"100%"}
                                                                position={
                                                                    "relative"
                                                                }
                                                                justifyContent={
                                                                    "center"
                                                                }
                                                            >
                                                                <Button
                                                                    variant={
                                                                        selectedSlotIndex ===
                                                                        index
                                                                            ? "contained"
                                                                            : "outlined"
                                                                    }
                                                                    onClick={() =>
                                                                        setSelectedSlotIndex(
                                                                            index
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        justifyContent:
                                                                            "center",
                                                                        flexDirection:
                                                                            "column",
                                                                        width: "100%",
                                                                        height: "42px",
                                                                        borderRadius:
                                                                            "40px",
                                                                        "&:hover":
                                                                            {
                                                                                backgroundColor:
                                                                                    selectedSlotIndex ===
                                                                                    index
                                                                                        ? theme
                                                                                              .palette
                                                                                              .primary
                                                                                              .main
                                                                                        : theme
                                                                                              .palette
                                                                                              .primary
                                                                                              .medium,
                                                                                cursor: "pointer"
                                                                            }
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            lineHeight:
                                                                                "16px",
                                                                            whiteSpace:
                                                                                "pre-wrap"
                                                                        }}
                                                                    >
                                                                        {`${formatInTimeZone(
                                                                            parseISO(
                                                                                isRepeating
                                                                                    ? slot.slots[0].timeSlotDateAndTime.replace(
                                                                                          /\d{2}:\d{2}(?=:\d{2}[+-]\d{2}:\d{2}$)/,
                                                                                          slot.timePattern
                                                                                      )
                                                                                    : slot.timeSlotDateAndTime
                                                                            ),
                                                                            isRepeating
                                                                                ? slot.slots[0].timeSlotDateAndTime
                                                                                      .replace(
                                                                                          /\d{2}:\d{2}(?=:\d{2}[+-]\d{2}:\d{2}$)/,
                                                                                          slot.timePattern
                                                                                      )
                                                                                      .match(
                                                                                          /[+-]\d{2}:\d{2}$/
                                                                                      )[0]
                                                                                : slot.timeSlotDateAndTime.match(
                                                                                      /[+-]\d{2}:\d{2}$/
                                                                                  )[0],
                                                                            "E, MMM dd – h:mm aa"
                                                                        )}`}
                                                                    </Typography>

                                                                    <Typography
                                                                        variant="helperText"
                                                                        sx={{
                                                                            fontSize:
                                                                                "12px",
                                                                            fontStyle:
                                                                                "italic",
                                                                            lineHeight:
                                                                                "14px",
                                                                            color:
                                                                                selectedSlotIndex ===
                                                                                index
                                                                                    ? "white"
                                                                                    : ""
                                                                        }}
                                                                    >
                                                                        {slot.travelType ===
                                                                        "ON-EMPTY-DAY"
                                                                            ? `Empty day, first booking`
                                                                            : slot.travelType ===
                                                                                "SAME-LOCATION-AS-PREVIOUS"
                                                                              ? `Same location as previous booking`
                                                                              : slot.travelType ===
                                                                                  "SAME-LOCATION-AS-NEXT"
                                                                                ? `Same location as next booking`
                                                                                : `${slot.travelTimeMins - DEFAULT_BUFFER_TIME_MINS} mins travel ${
                                                                                      slot.travelType ===
                                                                                      "FROM-PREVIOUS-BOOKING"
                                                                                          ? "from previous booking"
                                                                                          : "to next booking"
                                                                                  } (${(slot?.travelDistance || 0).toFixed(1)} ${distanceUnit}) + ${DEFAULT_BUFFER_TIME_MINS} mins buffer`}
                                                                    </Typography>
                                                                    <Tooltip
                                                                        placement="right"
                                                                        style={{
                                                                            position:
                                                                                "absolute",
                                                                            top: "-2px",
                                                                            right: "0px",
                                                                            transform:
                                                                                "translate(24%, -24%)"
                                                                        }}
                                                                        componentsProps={{
                                                                            tooltip:
                                                                                {
                                                                                    sx: {
                                                                                        whiteSpace:
                                                                                            "pre-line",
                                                                                        width: "175px"
                                                                                    }
                                                                                }
                                                                        }}
                                                                        arrow
                                                                        title={
                                                                            isRepeating &&
                                                                            slot.consistency <
                                                                                1 ? (
                                                                                <Typography
                                                                                    sx={{
                                                                                        lineHeight:
                                                                                            "16px"
                                                                                    }}
                                                                                >
                                                                                    {selectedPackage ? (
                                                                                        <div>
                                                                                            {`This time is only available for ${(slot?.slots?.length * slot?.consistency).toFixed(0)} out of ${slot?.slots?.length} appointments in this package`}
                                                                                        </div>
                                                                                    ) : (
                                                                                        `This time is not available for every appointment in the recurring set`
                                                                                    )}
                                                                                </Typography>
                                                                            ) : slot.travelTimeMins <=
                                                                                  5 &&
                                                                              slot.travelType !==
                                                                                  "ON-EMPTY-DAY" ? (
                                                                                <Typography>
                                                                                    Less
                                                                                    than
                                                                                    5
                                                                                    minutes
                                                                                    travel
                                                                                    time
                                                                                </Typography>
                                                                            ) : (
                                                                                ""
                                                                            )
                                                                        }
                                                                    >
                                                                        {isRepeating &&
                                                                        slot.consistency <
                                                                            1 ? (
                                                                            <span>
                                                                                <img
                                                                                    src={
                                                                                        WarningTriangleIcon
                                                                                    }
                                                                                    alt="Warning Triangle Icon"
                                                                                    style={{
                                                                                        height: "22px",
                                                                                        width: "22px",
                                                                                        position:
                                                                                            "absolute",
                                                                                        top: "-2px",
                                                                                        right: "0px",
                                                                                        transform:
                                                                                            "translate(24%, -24%)"
                                                                                    }}
                                                                                />
                                                                            </span>
                                                                        ) : (slot.travelTimeMins <=
                                                                              5 &&
                                                                              slot.travelType !==
                                                                                  "ON-EMPTY-DAY") ||
                                                                          slot.travelType.startsWith(
                                                                              "SAME-LOCATION"
                                                                          ) ? (
                                                                            <span>
                                                                                <LeafSvg />
                                                                            </span>
                                                                        ) : null}
                                                                    </Tooltip>
                                                                </Button>
                                                            </Box>
                                                        </ThemeProvider>
                                                    )
                                                )}
                                            </Grid>
                                        </Box>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {showSlots && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            paddingTop: "8px"
                                        }}
                                    >
                                        <Typography>No slots found</Typography>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    paddingTop: "34px",
                    gap: "10px",
                    [theme.breakpoints.down("md")]: {
                        padding: "18px"
                    }
                }}
            >
                <Button variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    onClick={() => {
                        if (provider && displayedSlots[selectedSlotIndex]) {
                            if (
                                displayedSlots[selectedSlotIndex].consistency <
                                1
                            ) {
                                setShowPartialAvailabilityDialog(true);
                            } else {
                                handleClientAutoFocus();
                                onDateTimeChange(
                                    isRepeating
                                        ? displayedSlots[selectedSlotIndex]
                                              .slots
                                        : new Date(
                                              displayedSlots[
                                                  selectedSlotIndex
                                              ].timeSlotDateAndTime
                                          ),
                                    getSlotType()
                                );
                            }
                        } else {
                            handleClientAutoFocus();
                            handleRegularConfirm();
                        }
                    }}
                >
                    Confirm
                </LoadingButton>
            </DialogActions>
            {showPartialAvailabilityDialog && (
                <PartialAvailabilityDialog
                    onClose={() => setShowPartialAvailabilityDialog(false)}
                    selectedSlot={displayedSlots[selectedSlotIndex]}
                    onDateTimeChange={onDateTimeChange}
                    handleClientAutoFocus={handleClientAutoFocus}
                    showPartialAvailabilityDialog={
                        showPartialAvailabilityDialog
                    }
                />
            )}
        </>
    );
};

const PartialAvailabilityDialog = ({
    onClose,
    selectedSlot,
    onDateTimeChange,
    showPartialAvailabilityDialog,
    handleClientAutoFocus
}) => {
    const [{ selectedPackage }] = useAtom(bookingDetailsAtom);
    const alternativeSlots = selectedSlot.slots.filter((slot) => {
        if (!selectedSlot.timePattern) return false;

        const timePart = slot.timeSlotDateAndTime.match(/T(\d{2}:\d{2})/)[1];
        return timePart !== selectedSlot.timePattern;
    });

    const getPartialAvailabilityMessage = (alternativeSlots) => {
        if (selectedPackage) {
            return (
                <>
                    <Typography>
                        {selectedSlot.providerFirstName} can only do the
                        selected time for{" "}
                        <span style={{ fontWeight: "bold" }}>
                            {" "}
                            {selectedSlot?.slots?.length -
                                alternativeSlots?.length}{" "}
                            out of {selectedSlot?.slots?.length} appointments in
                            this package{" "}
                        </span>{" "}
                        – for the {alternativeSlots?.length} remaining{" "}
                        {alternativeSlots?.length === 1 ? "date" : "dates"}{" "}
                        listed below, the system will find the closes possible
                        alternative and you can delete or override those
                        manually if necessary:
                    </Typography>
                    <Grid container spacing={2.25}>
                        {alternativeSlots.map((slot, index) => (
                            <Grid item key={index} xs={12}>
                                {" "}
                                <ul
                                    style={{
                                        paddingLeft: 36,
                                        margin: 0
                                    }}
                                >
                                    <li>
                                        <Typography>
                                            {formatInTimeZone(
                                                parseISO(
                                                    slot.timeSlotDateAndTime
                                                ),
                                                slot.timeSlotDateAndTime.match(
                                                    /[+-]\d{2}:\d{2}$/
                                                )[0],
                                                "E, MMM dd, yyyy"
                                            )}
                                        </Typography>
                                    </li>
                                </ul>
                            </Grid>
                        ))}
                    </Grid>
                </>
            );
        } else {
            return (
                <>
                    <Typography>
                        {selectedSlot.providerFirstName} can only do the
                        selected time for{" "}
                        <span style={{ fontWeight: "bold" }}>
                            {" "}
                            {selectedSlot?.slots?.length -
                                alternativeSlots?.length}{" "}
                            out of {selectedSlot?.slots?.length} appointments in
                            this recurring set{" "}
                        </span>{" "}
                        – the system will find the closest possible alternative
                        for the remaining {alternativeSlots?.length}{" "}
                        appointments and you can delete or override those
                        manually if necessary
                    </Typography>
                    {selectedSlot?.slots?.length >= 10 && (
                        <Typography sx={{ paddingTop: "18px" }}>
                            <span style={{ fontWeight: "bold" }}>WARNING:</span>{" "}
                            there may be other conflicts after the 10th
                            appointment, but our system cannot look that far
                            ahead
                        </Typography>
                    )}
                </>
            );
        }
    };

    const getSlotType = () => {
        if (selectedSlot?.travelType === "ON-EMPTY-DAY") {
            return SLOT_TYPES.SUGGESTED_SLOT_EMPTY_DAY;
        } else if (
            selectedSlot?.travelTimeMins <= 5 ||
            selectedSlot?.travelType.startsWith("SAME-LOCATION")
        ) {
            return SLOT_TYPES.SUGGESTED_SLOT_OPTIMIZED;
        } else {
            return SLOT_TYPES.SUGGESTED_SLOT;
        }
    };

    return (
        <Dialog open={showPartialAvailabilityDialog} onClose={onClose}>
            <DialogTitle sx={{ paddingBottom: "32px" }}>
                Partial availability
            </DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
                {getPartialAvailabilityMessage(alternativeSlots)}
                <Grid container spacing={2.25}>
                    <Grid item xs={12}>
                        <Typography>
                            Click “Cancel” to select a different time slot or
                            provider
                        </Typography>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ paddingTop: "34px", gap: "10px" }}>
                <Button variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    onClick={() => {
                        handleClientAutoFocus();
                        onDateTimeChange(selectedSlot.slots, getSlotType());
                    }}
                >
                    Continue
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};
